import { useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';

import { toast } from '@hivebrite/design-system/Toast';

import CardJob from 'hb-react/frontoffice/apps/JobsV2/components/CardJob';
import CardJobSmall from 'hb-react/frontoffice/apps/JobsV2/components/CardJobSmall';
import getPageCustomizableHeaderProps from 'hb-react/shared/apps/PageCustomizables/utils/getPageCustomizableHeaderProps';
import ButtonInLink from 'hb-react/shared/components/DesignSystem/ButtonInLink';
import MoreInfoButton from 'hb-react/shared/components/MoreInfoButton';
import { useQueryOtherInfos } from 'hb-react/shared/hooks/useQueryOtherInfos';

import {
  contractTypeFilter,
  earliestStartDateFilter,
  hybridFilter,
  keywordFilter,
  keywordOrIndustriesFilter,
  locationFilter,
  onSiteFilter,
  organizationFilter,
  remoteFilter,
  remoteOnSiteFiltersHeader,
  yearsOfExperienceFilter,
} from './filters';
import { makeDeleteItem } from '../requests';

export const makeConfigUrls = ({ currentUser }) => ({
  pathRoot: '/opportunities',
  editItem: ({ opportunityId }) => Routes.edit_opportunity_path(opportunityId),
  deleteItem: ({ opportunityId }) =>
    Routes.user_business_opportunities_request_path(
      currentUser.id,
      opportunityId,
    ),
  fetchItem: ({ opportunityId }) =>
    currentUser
      ? Routes.user_business_opportunities_request_path(
          currentUser.id,
          opportunityId,
        )
      : Routes.opportunity_path(opportunityId),
  fetchItems: currentUser
    ? Routes.user_business_opportunities_requests_path(currentUser.id)
    : Routes.opportunities_path(),
  fetchTagOptions: currentUser
    ? Routes.tag_options_user_business_opportunities_requests_path(
        currentUser.id,
      )
    : Routes.tag_options_opportunities_path(),
});

export const makeItemRenderer =
  ({
    currentNetwork,
    currentUser,
    onDeleteItem,
    urls,
    variant = 'normal',
    onCompanyPage = false,
    getSelectedId = null,
  }) =>
  ({ item, data }) => {
    const location = useLocation();
    const params = useParams();

    const selectedId = getSelectedId ? getSelectedId(data, params) : null;
    const { search } = location;

    const deleteItem = useMemo(() => makeDeleteItem({ urls }), []);
    const jobKinds = useQueryOtherInfos({
      getter: (data) => data?.job_kind_configurations,
    });
    const jobKindConfig = useMemo(() => jobKinds, [jobKinds]);

    const CardComponent = variant === 'small' ? CardJobSmall : CardJob;

    return (
      // eslint-disable-next-line react/jsx-filename-extension
      <CardComponent
        key={item.id}
        currentNetwork={currentNetwork}
        currentUser={currentUser}
        to={`${urls.pathRoot}/${item.id}${search}`}
        urls={urls}
        onDeleteItem={(v) =>
          deleteItem(v).then(() => {
            onDeleteItem?.(item.company_id);
            toast.success(__('Successfully deleted'));
          })
        }
        item={item}
        jobKindConfig={jobKindConfig}
        isSelected={item.id === selectedId}
        onCompanyPage={onCompanyPage}
      />
    );
  };

export const getFilters = (currentNetwork) =>
  // Filters V2
  [
    currentNetwork?.user_fields_config?.display_industries
      ? keywordOrIndustriesFilter(currentNetwork)
      : keywordFilter,
    locationFilter,
    contractTypeFilter,
    earliestStartDateFilter,
    yearsOfExperienceFilter,
    organizationFilter,
    remoteOnSiteFiltersHeader,
    onSiteFilter,
    remoteFilter,
    hybridFilter,
  ].filter(Boolean);

export const getJobsHeaderProps = ({
  appPageConfig,
  currentUser,
  isPreview,
  urls,
}) => {
  const { controls } = getJobsHeaderControls({
    appPageConfig,
    displayBizoppButtons: currentUser?.display_bizopp_buttons,
    isPreview,
    urls,
  });

  return getPageCustomizableHeaderProps({ appPageConfig, controls });
};

export const getJobsHeaderControls = ({
  appPageConfig,
  displayBizoppButtons,
  isPreview,
  urls,
}) => {
  if (!appPageConfig) {
    return {};
  }

  const {
    html_block_active: htmlBlockActive,
    html_block_content: htmlBlockContent,
  } = appPageConfig;

  const controls = [
    displayBizoppButtons && (
      <ButtonInLink
        wrapperProps={{ style: { width: '100%' } }}
        linkProps={{
          to: isPreview ? '#' : `${urls.pathRoot}/new`,
          style: { width: '100%' },
        }}
      >
        {s__('Jobs|Publish your opportunity')}
      </ButtonInLink>
    ),
    htmlBlockActive && htmlBlockContent && (
      <MoreInfoButton htmlBlock={htmlBlockContent} variant="secondary" />
    ),
  ].filter(Boolean);

  return {
    controls,
  };
};
